import React from 'react';
import LeaderboardPreview from './LeaderboardPreview';
import EventsPreview from './EventsPreview';
import Header from './Header';
import './LandingPage.css';

const featureRows = [
  {
    title: 'Sell online & in-venue',
    copy: 'Branded booking pages, walk-ons, private parties, POS-ready checkout, memberships, and deposits that keep calendars full.',
    chips: ['Online Ticketing', 'POS', 'Memberships', 'Deposits'],
  },
  {
    title: 'Serve your guests',
    copy: 'Digital waivers, access control, group surveys, and organizer tools so guests can self-serve before they arrive.',
    chips: ['Waivers', 'Access Control', 'Guest Surveys', 'Group Portal'],
  },
  {
    title: 'Market your venues',
    copy: 'Win-back automation, loyalty, segmented campaigns, and pricing tools that bring players back without extra admin.',
    chips: ['Loyalty', 'Channel Management', 'Pricing Strategy', 'Win-Back Email'],
  },
  {
    title: 'Manage business operations',
    copy: 'Payments, HQ controls, reporting, event calendars, and mobile workflows that run the field from anywhere.',
    chips: ['Payments', 'HQ', 'Reporting & Analytics', 'Mobile Ops'],
  },
  {
    title: 'AI for attractions',
    copy: 'AI-assisted guest experiences, iQ-style insights, and support tools that help your team move faster than the crowd.',
    chips: ['iQ', 'Guest Experience Agent', 'Ops Assist', 'Insights'],
  },
];

const comparisonRows = [
  [
    '24/7 branded booking',
    'Yes',
    'Yes',
    'Yes',
  ],
  [
    'Built-in waivers and parent signatures',
    'Yes',
    'Yes',
    'Yes',
  ],
  [
    'Automatic win-back / follow-up campaigns',
    'Yes',
    'Partial',
    'Partial',
  ],
  [
    'Group organizer portal',
    'Yes',
    'Partial',
    'Partial',
  ],
  [
    'Direct-to-bank payment flow',
    'Yes',
    'Yes',
    'Partial',
  ],
  [
    'Scenario events, tournaments, and big games',
    'Yes',
    'Partial',
    'No',
  ],
];

function LandingPage() {
  return (
    <div className="landing-page">
      <Header />
      <div className="page-content">
        <section className="hero">
          <div className="hero-overlay" />
          <div className="hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">PBHQ-level operations. More complete. More automated.</p>
              <h1 className="hero-title">Run bookings, waivers, events, and guest marketing from one system.</h1>
              <p className="hero-subtitle">
                A venue OS for paintball, attractions, and action sports that turns every first visit into a repeat visit.
                Sell online and in-venue, move group organizers into self-service, and keep your revenue flowing directly to your bank.
              </p>

              <div className="hero-actions">
                <a className="primary-cta" href="#features">See the stack</a>
                <a className="secondary-cta" href="#comparison">Compare features</a>
              </div>

              <div className="hero-proof">
                <span>24/7 online booking</span>
                <span>Digital waivers</span>
                <span>Organizer dashboard</span>
                <span>Mobile-friendly ops</span>
              </div>
            </div>

            <div className="hero-panel card">
              <div className="hero-panel-top">
                <span className="panel-label">Venue command center</span>
                <span className="panel-pill">Live</span>
              </div>
              <div className="hero-panel-stats">
                <div>
                  <strong>Bookings</strong>
                  <span>Walk-ons + groups</span>
                </div>
                <div>
                  <strong>Waivers</strong>
                  <span>Signed before arrival</span>
                </div>
                <div>
                  <strong>Marketing</strong>
                  <span>Automated follow-up</span>
                </div>
              </div>
              <div className="hero-panel-card">
                <p>Free playbook: 7 fixes every sports facility needs to grow faster</p>
                <a href="#comparison">Download the comparison</a>
              </div>
            </div>
          </div>
        </section>

        <section className="section feature-grid" id="features">
          <div className="section-heading">
            <p className="section-kicker">What operators get</p>
            <h2 className="section-title">The feature set PBHQ, Roller, and SportsCarnival hint at, fully connected.</h2>
          </div>

          <div className="feature-list">
            {featureRows.map((feature) => (
              <article key={feature.title} className="feature-card card">
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
                <div className="chip-row">
                  {feature.chips.map((chip) => (
                    <span key={chip}>{chip}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section comparison-section" id="comparison">
          <div className="section-heading">
            <p className="section-kicker">Comparison</p>
            <h2 className="section-title">Match the basics. Exceed the workflow.</h2>
          </div>

          <div className="comparison-table card">
            <div className="comparison-head">
              <span>Capability</span>
              <span>RKE.world</span>
              <span>PBHQ</span>
              <span>Roller / SportsCarnival</span>
            </div>
            {comparisonRows.map(([label, ours, pbhq, others]) => (
              <div key={label} className="comparison-row">
                <span>{label}</span>
                <span className="yes">{ours}</span>
                <span>{pbhq}</span>
                <span>{others}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="section two-up">
          <div>
            <div className="section-heading">
              <p className="section-kicker">Events</p>
              <h2 className="section-title">Big games, tournaments, and scenario weekends.</h2>
            </div>
            <EventsPreview />
          </div>

          <div>
            <div className="section-heading">
              <p className="section-kicker">Proof</p>
              <h2 className="section-title">Players come back when the field remembers them.</h2>
            </div>
            <LeaderboardPreview />
          </div>
        </section>

        <footer className="footer">
          <p>© 2026 Ronin Kinetic eSports. Built for facilities that want more than a booking link.</p>
          <p><a href="/privacy">Privacy & Data Requests</a></p>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
